// @flow
import React from 'react'
import I18n from 'i18n-react'
import { humanizer, transform } from 'devx-js-utilities'

import { WEB_TYPES_KEYS } from '../Redux/SettingsRedux'

// core
import type { InsuranceCompaniesType } from '../Types/InsuranceCompanyTypes'
import type { SelectedCoveragePrices } from '../Redux/Types/QuoteStateType'
import type QuoteInstance from '../Controllers/QuoteInstance'

type EmailOffersProps = {
  coveragePrices: SelectedCoveragePrices,
  debug?: boolean,
  id?: string,
  insuranceCompanies: InsuranceCompaniesType,
  name?: string,
  onRemove: (number) => void,
  onSelect: (QuoteInstance) => void,
  quotes: Array<QuoteInstance>,
  supplementaryInsurances: { [key: string]: any },
  webType: number,
}

type EmailOffersState = {
  email: string,
  showModal: boolean,
}

export default class EmailOffers extends React.Component<EmailOffersProps, EmailOffersState> {
  state = {
    email: '',
    showModal: false,
  }

  handleSelectQuote = (quote: QuoteInstance) => {
    this.props.onSelect(quote)
  }

  handleOnRemove = (index: number) => {
    this.props.onRemove(index)
  }

  render = () => {
    const { quotes, webType, insuranceCompanies, supplementaryInsurances, coveragePrices } = this.props

    return (
      <div className="card card-with-edit flex-xl-column justify-content-between with-default-bg px-3 py-3 mt-4">
        <div className="title border-bottom mb-3">
          <strong className="lead">{I18n.translate('components.emailOffer.title')}</strong>
        </div>

        {quotes.map((quote: QuoteInstance, index) => {
          const insuranceCompany = {}
          let priceNumber = quote.price
          if (coveragePrices && coveragePrices[quote.uniqueQuoteId]) {
            priceNumber = coveragePrices[quote.uniqueQuoteId].fullPrice
          }
          Object.keys(quote.subProductToTariffName).map((tab) => {
            if (insuranceCompanies[quote.tariffContent[tab].insuranceCompany]) {
              insuranceCompany[tab] = insuranceCompanies[quote.tariffContent[tab].insuranceCompany]
            }
          })

          return [
            <div className="card-header p-0 border-0 mb-2 d-flex justify-content-between" key={`offer-${index}-title`}>
              <strong className="d-xl-inline-block">
                {I18n.translate('components.emailOffer.counter', { index: index + 1 })}
              </strong>
              <a className="edit-btn text-nowrap align-items-center" onClick={() => this.handleOnRemove(index)}>
                <strong>
                  <u>{I18n.translate('components.emailOffer.remove')}</u>
                </strong>
              </a>
            </div>,
            <div className="row" key={`offer-${index}-content`}>
              <div className="col-12 col-sm-6 col-xxl-12 mb-2">
                {quote.tariffContent.tpl && insuranceCompany.tpl ? (
                  <div>
                    <strong className="d-block mb-1">{I18n.translate('general.tpl')}:</strong>
                    <div className="row flex-nowrap align-items-center">
                      <div className="col-8 pr-0">
                        {!!insuranceCompany.tpl.webLogo && (
                          <img
                            alt={insuranceCompany.tpl.name}
                            className="d-sm-none d-lg-inline-block"
                            key={`quote-insuranceCompany-logo-${insuranceCompany.tpl.name}`}
                            src={
                              quote.tariffContent.tpl.icon
                                ? quote.tariffContent.tpl.icon[WEB_TYPES_KEYS[webType]]
                                : insuranceCompany.tpl.webLogo
                            }
                            width={60}
                          />
                        )}
                        <span className="ml-2">{quote.tariffContent.tpl.name[WEB_TYPES_KEYS[webType]]}</span>
                      </div>
                      <div className="col-4 text-right pl-1">
                        <span>
                          {I18n.translate('components.emailOffer.tpl.limit', {
                            health: quote.tariffContent.tpl.properties.limits.health,
                            property: quote.tariffContent.tpl.properties.limits.property,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <i>{I18n.translate('components.emailOffer.tpl.unselected')}</i>
                )}
              </div>
              <div className="col-12 col-sm-6 col-xxl-12 mb-2">
                {quote.tariffContent.cic && insuranceCompany.cic ? (
                  <div>
                    <strong className="d-block mb-1">{I18n.translate('general.cic')}:</strong>
                    <div className="row flex-nowrap align-items-center">
                      <div className="col-8 pr-0">
                        {!!insuranceCompany.cic.webLogo && (
                          <img
                            alt={insuranceCompany.cic.name}
                            className="d-sm-none d-lg-inline-block"
                            key={`quote-insuranceCompany-logo-${insuranceCompany.cic.name}`}
                            src={
                              quote.tariffContent.cic.icon
                                ? quote.tariffContent.cic.icon[WEB_TYPES_KEYS[webType]]
                                : insuranceCompany.cic.webLogo
                            }
                            width={60}
                          />
                        )}
                        <span className="ml-2">{quote.tariffContent.cic.name[WEB_TYPES_KEYS[webType]]}</span>
                      </div>
                      <div className="col-4 text-right pl-1">
                        <span className="d-lg-block">
                          {quote.tariffContent.cic.properties.limits.percent > 0 &&
                            quote.tariffContent.cic.properties.limits.percent + '%'}
                        </span>
                        <span className="d-lg-block">
                          {quote.tariffContent.cic.properties.limits.value > 0 &&
                            humanizer.csNumber(quote.tariffContent.cic.properties.limits.value)}
                          &nbsp;
                          {I18n.translate('general.currency')}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <i>{I18n.translate('components.emailOffer.cic.unselected')}</i>
                )}
              </div>
              <div className="col-12 col-sm-6 col-xxl-12 mb-2">
                {supplementaryInsurances[quote.uniqueQuoteId] ? (
                  <div>
                    <strong className="d-block mb-1">{I18n.translate('general.supplementaryInsurance')}:</strong>
                    {Object.keys(supplementaryInsurances[quote.uniqueQuoteId]).map((supplementary) => (
                      <p key={supplementary}>
                        {I18n.translate(
                          `coverages.${supplementary}${transform.capitalize(quote.insuranceCompany)}.title`,
                          {
                            notFound: I18n.translate(`coverages.${supplementary}.title`),
                          },
                        )}
                      </p>
                    ))}
                  </div>
                ) : (
                  <i>{I18n.translate('components.emailOffer.supp.unselected')}</i>
                )}
              </div>
              <div className="col-12 col-sm-6 col-xxl-12 mb-2 mt-2">
                <div className="row flex-nowrap align-items-center">
                  <div className="col-7 pr-0">
                    <strong>{I18n.translate('general.totalPrice', { price: humanizer.csNumber(priceNumber) })}</strong>
                  </div>
                  <div className="col-5 text-right pl-1">
                    <button
                      className="btn btn-secondary px-3"
                      onClick={() => this.handleSelectQuote(quote)}
                      type="button"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            <hr className="border-top" key={`offer-${index}-end`} />,
          ]
        })}
      </div>
    )
  }
}
